import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Game } from '../types/index.js';
import { useBrand } from '../hooks/useBrand.js';
import { computeStandings } from '../services/scoring.js';
import { TeamSquare } from './TeamSquare.js';

interface ScoreboardProps {
  game: Game;
  /** Called when a score square is tapped — passes the team id (authenticated flow) */
  onScoreSquareTap?: (teamId: 'team1' | 'team2') => void;
  /**
   * Zone tap handler — upper half = +1, lower half = −1.
   * Used by anonymous simple mode. Mutually exclusive with onScoreSquareTap.
   */
  onScoreZoneTap?: (teamId: 'team1' | 'team2', delta: 1 | -1) => void;
  /**
   * Override computed scores — used by anonymous simple mode where scores
   * are tracked locally instead of via game.rounds.
   */
  team1ScoreOverride?: number;
  team2ScoreOverride?: number;
  /** Briefly highlight when an external update arrives */
  flashExternal?: boolean;
  onFlashDone?: () => void;
}

/**
 * Main scoreboard display — designed for 10" landscape tablet.
 *
 * Layout (% of total height):
 *  ~15%  — player A names (above score squares) — ALWAYS present
 *  ~70%  — score squares (colored, jumbotron-style numbers)
 *            └── floating logo overlays the top boundary (~15% mark)
 *            └── floating round badge overlays the bottom boundary
 *  ~15%  — player B names (doubles) / empty spacer (singles) — ALWAYS present
 *
 * Font sizing: CSS container queries (cqh units) — container-relative, NOT
 * viewport-relative. This ensures correct scaling at any tablet resolution or
 * window size. Score numbers = 75% of score square height (≈52.5cqh relative to
 * TeamSquare container). Player names use dynamic fit-text: start large,
 * shrink to fit 35vw, and all names share the same (smallest) computed size
 * so the scoreboard stays visually balanced.
 *
 * Brand-themed via useBrand(). Touch-optimized — large tap targets.
 */
export function Scoreboard({ game, onScoreSquareTap, onScoreZoneTap, team1ScoreOverride, team2ScoreOverride, flashExternal, onFlashDone }: ScoreboardProps) {
  const brand = useBrand();
  const computed = computeStandings(game.rounds);
  const team1Score = team1ScoreOverride ?? computed.team1Score;
  const team2Score = team2ScoreOverride ?? computed.team2Score;
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    if (flashExternal) {
      setFlashing(true);
      const t = setTimeout(() => {
        setFlashing(false);
        onFlashDone?.();
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [flashExternal, onFlashDone]);

  const isDoubles = game.team1.players.length > 1;

  // Show logo if at least one logo URL is configured (non-empty string)
  // Prefer full (real branded logo) over icon (small square placeholder)
  const logoUrl = brand.logos.full || brand.logos.icon || '';

  // ── Shared name font-size logic ────────────────────────────────────────
  // Each TeamSquare reports the computed fit size for each of its player
  // name slots.  Once all expected slots have reported in, we take the
  // minimum and pass it back down so every name renders at the same size.
  // This keeps the scoreboard visually balanced regardless of name length.

  const [nameSizes, setNameSizes] = useState<{
    t1a?: number; t2a?: number; t1b?: number; t2b?: number;
  }>({});

  // Reset measurements whenever the actual name strings change (new game,
  // player edit, etc.) so we re-measure from scratch.
  const playerNamesKey = [
    game.team1.players[0]?.name,
    game.team1.players[1]?.name,
    game.team2.players[0]?.name,
    game.team2.players[1]?.name,
  ].join('|');

  useEffect(() => {
    setNameSizes({});
  }, [playerNamesKey]);

  // Stable callbacks so TeamSquare props don't thrash on every render
  const handleTeam1NameSize = useCallback((which: 'a' | 'b', size: number) => {
    setNameSizes(prev => ({ ...prev, [which === 'a' ? 't1a' : 't1b']: size }));
  }, []);

  const handleTeam2NameSize = useCallback((which: 'a' | 'b', size: number) => {
    setNameSizes(prev => ({ ...prev, [which === 'a' ? 't2a' : 't2b']: size }));
  }, []);

  // Only apply the shared size once ALL expected slots have reported.
  // This prevents a flash where some names are large and others small.
  const sharedFontSize = useMemo(() => {
    const slots: (number | undefined)[] = [nameSizes.t1a, nameSizes.t2a];
    if (isDoubles) slots.push(nameSizes.t1b, nameSizes.t2b);
    const allReady = slots.every(s => s != null && s > 0);
    if (!allReady) return undefined;
    return Math.min(...(slots as number[]));
  }, [nameSizes, isDoubles]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: brand.colors.background,
        color: brand.colors.text,
        fontFamily: "'Inter', sans-serif",
        userSelect: 'none',
        overflow: 'hidden',
      }}
      aria-label="Scoreboard"
    >
      {/*
       * Main scoring area — fills full height.
       * container-type: size makes this a size-query container.
       * Floating overlays (logo + round badge) use cqh relative to THIS
       * container's height, so they track the scoreboard proportionally
       * at any resolution — no vh involved.
       */}
      <div
        style={{
          display: 'flex',
          flex: 1,
          gap: '16px',
          padding: '0 16px',
          outline: flashing ? `6px solid ${brand.colors.secondary}` : '6px solid transparent',
          transition: 'outline-color 0.2s ease',
          minHeight: 0, // allow flex children to shrink
          position: 'relative',
          containerType: 'size',
        }}
      >
        {/*
         * Floating logo — overlays the boundary between the top name area
         * and the score squares. Positioned at 15cqh (= 15% of this
         * container's height), matching the name zone proportion exactly.
         * pointer-events: none so it never blocks taps.
         */}
        {logoUrl && (
          <img
            src={logoUrl}
            alt={brand.shortName}
            style={{
              position: 'absolute',
              top: '15cqh',
              left: '50%',
              transform: 'translateX(-50%) translateY(-50%)',
              height: '20cqh',
              width: 'auto',
              objectFit: 'contain',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Team 1 — left side */}
        <TeamSquare
          teamId="team1"
          team={game.team1}
          score={team1Score}
          color={brand.colors.scoreboard.left}
          textColor={brand.colors.text}
          isDoubles={isDoubles}
          onTap={onScoreZoneTap ? undefined : onScoreSquareTap}
          onZoneTap={onScoreZoneTap}
          onNameSize={handleTeam1NameSize}
          sharedFontSize={sharedFontSize}
        />

        {/* Team 2 — right side */}
        <TeamSquare
          teamId="team2"
          team={game.team2}
          score={team2Score}
          color={brand.colors.scoreboard.right}
          textColor={brand.colors.text}
          isDoubles={isDoubles}
          onTap={onScoreZoneTap ? undefined : onScoreSquareTap}
          onZoneTap={onScoreZoneTap}
          onNameSize={handleTeam2NameSize}
          sharedFontSize={sharedFontSize}
        />

        {/*
         * Floating round badge — hidden when score overrides are active
         * (anonymous simple mode — no rounds, no PPR).
         */}
        {team1ScoreOverride == null && <div
          style={{
            position: 'absolute',
            bottom: '8cqh',
            left: '50%',
            transform: 'translateX(-50%) translateY(50%)',
            backgroundColor: '#000',
            color: '#fff',
            borderRadius: '20px',
            padding: '12px 32px',
            textAlign: 'center',
            zIndex: 10,
            pointerEvents: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            whiteSpace: 'nowrap',
            minWidth: '14cqh',
          }}
          aria-label={`${brand.terms.round} ${game.currentRound}`}
        >
          <div
            style={{
              fontSize: '1.8cqh',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              opacity: 0.85,
              lineHeight: 1.2,
            }}
          >
            {brand.terms.round}
          </div>
          <div
            style={{
              fontSize: '13cqh',
              fontWeight: 900,
              lineHeight: 0.9,
              marginTop: '-0.05em',
            }}
          >
            {game.currentRound}
          </div>
        </div>}
      </div>
    </div>
  );
}
